/**
 * Focused SP loan-value lookup page.
 */
import { formatCurrency, formatInteger } from "./format.js";
import { escapeAttr, escapeHtml, labelize, pill } from "./dashboard-html.js";
import { authenticatedFetch } from "./dashboard-auth.js";
import { toolbenchRunsUrl, spLookupPollIntervalMs } from "./dashboard-urls.js";
import type { ToolbenchRun } from "./dashboard-lead-factory-types.js";

function unknownRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

export function bindDirectSpLookup(): void {
  const form = document.querySelector<HTMLFormElement>("[data-sp-lookup-form]");
  const status = document.querySelector<HTMLElement>("[data-sp-lookup-status]");
  const result = document.querySelector<HTMLElement>("[data-sp-lookup-result]");
  const history = document.querySelector<HTMLElement>("[data-sp-lookup-history]");
  if (!form || !status || !result || !history) return;
  let pollTimer: number | null = null;
  const stopPolling = (): void => {
    if (pollTimer !== null) window.clearTimeout(pollTimer);
    pollTimer = null;
  };
  const pollUntilTerminal = (runId: string): void => {
    stopPolling();
    const poll = async (): Promise<void> => {
      if (!document.body.contains(result)) return;
      const run = await refreshDirectSpLookup(runId, status, result);
      if (!run) {
        pollTimer = window.setTimeout(() => void poll(), spLookupPollIntervalMs);
        return;
      }
      if (!spLookupIsActive(run)) return;
      pollTimer = window.setTimeout(() => void poll(), spLookupPollIntervalMs);
    };
    pollTimer = window.setTimeout(() => void poll(), spLookupPollIntervalMs);
  };
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void submitDirectSpLookup(form, status, result, history, pollUntilTerminal);
  });
  history.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-sp-lookup-open]");
    if (!button) return;
    stopPolling();
    void (async () => {
      const run = await refreshDirectSpLookup(button.dataset.spLookupOpen ?? "", status, result);
      if (run && spLookupIsActive(run)) pollUntilTerminal(run.id);
    })();
  });
  result.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-sp-lookup-open]");
    if (button) {
      stopPolling();
      void (async () => {
        const run = await refreshDirectSpLookup(button.dataset.spLookupOpen ?? "", status, result);
        if (run && spLookupIsActive(run)) pollUntilTerminal(run.id);
      })();
      return;
    }
    const retryButton = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-sp-lookup-retry]");
    if (retryButton) void retryDirectSpLookup(retryButton.dataset.spLookupRetry ?? "", status, result, pollUntilTerminal);
  });
}

export async function submitDirectSpLookup(
  form: HTMLFormElement,
  status: HTMLElement,
  result: HTMLElement,
  history: HTMLElement,
  pollUntilTerminal: (runId: string) => void
): Promise<void> {
  const spNumber = String(new FormData(form).get("spNumber") ?? "").trim();
  if (!spNumber) {
    status.textContent = "Enter an SP number.";
    return;
  }
  status.textContent = `Submitting ${spNumber}...`;
  const response = await authenticatedFetch(toolbenchRunsUrl.split("?")[0] ?? "/dashboard/toolbench/runs", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ toolId: "sp_code_lookup", input: { spCode: spNumber } })
  });
  const body = (await response.json().catch(() => ({}))) as Partial<{ run: ToolbenchRun; error: string; message: string }>;
  if (!response.ok || !body.run) {
    status.textContent = body.message || body.error || `SP lookup failed: ${response.status}`;
    return;
  }
  status.textContent = spLookupStatusMessage(body.run);
  result.innerHTML = spLookupResultHtml(body.run);
  history.querySelector(".empty")?.remove();
  history.insertAdjacentHTML("afterbegin", spLookupHistoryHtml(body.run));
  updateSpLookupHistoryCount(history);
  if (spLookupIsActive(body.run)) pollUntilTerminal(body.run.id);
}

export async function refreshDirectSpLookup(runId: string, status: HTMLElement, result: HTMLElement): Promise<ToolbenchRun | null> {
  if (!runId) return null;
  status.textContent = `Refreshing ${runId}...`;
  const response = await authenticatedFetch(`/dashboard/toolbench/runs/${encodeURIComponent(runId)}`);
  const body = (await response.json().catch(() => ({}))) as Partial<{ run: ToolbenchRun; error: string }>;
  if (!response.ok || !body.run) {
    status.textContent = body.error || `Could not refresh lookup: ${response.status}`;
    return null;
  }
  status.textContent = spLookupStatusMessage(body.run);
  result.innerHTML = spLookupResultHtml(body.run);
  return body.run;
}

export async function retryDirectSpLookup(
  runId: string,
  status: HTMLElement,
  result: HTMLElement,
  pollUntilTerminal: (runId: string) => void
): Promise<void> {
  if (!runId) return;
  status.textContent = `Retrying ${runId}...`;
  const response = await authenticatedFetch(`/dashboard/toolbench/runs/${encodeURIComponent(runId)}/sp-retry`, { method: "POST" });
  const body = (await response.json().catch(() => ({}))) as Partial<{ run: ToolbenchRun; error: string }>;
  if (!response.ok || !body.run) {
    status.textContent = body.error || `Could not retry lookup: ${response.status}`;
    return;
  }
  status.textContent = "Lookup queued for retry.";
  result.innerHTML = spLookupResultHtml(body.run);
  if (spLookupIsActive(body.run)) pollUntilTerminal(body.run.id);
}

export function spLookupIsActive(run: ToolbenchRun): boolean {
  const lookupStatus = String(run.result?.lookup_status ?? run.status);
  return run.status === "queued" || run.status === "running" || lookupStatus === "queued" || lookupStatus === "running";
}

export function spLookupStatusMessage(run: ToolbenchRun): string {
  const lookupStatus = String(run.result?.lookup_status ?? run.status);
  const labels: Record<string, string> = {
    queued: "Lookup is queued and will start shortly.",
    running: "Lookup is running.",
    searching_case: "Searching the NC eCourts case.",
    retrieving_case_inventory: "Retrieving the case document inventory.",
    classifying_sale_status: "Checking the latest sale status.",
    retrieving_notice: "Retrieving the current Notice of Sale or Resale.",
    validating_evidence: "Validating the source document and evidence.",
    extracting_facts: "Extracting mortgage facts from the source documents.",
    calculating: "Calculating the present loan-value proxy.",
    complete: "Lookup complete.",
    sale_cancelled: "Sale canceled. This is a terminal case status, not a pending lookup.",
    sale_canceled: "Sale canceled. This is a terminal case status, not a pending lookup.",
    mortgage_evidence_incomplete: "Lookup needs review because required mortgage evidence is incomplete."
  };
  return labels[lookupStatus] ?? `Lookup is ${labelize(lookupStatus)}.`;
}

export function spLookupHistoryHtml(run: ToolbenchRun): string {
  const spNumber = run.result?.normalized_sp_number ?? run.input.spCode ?? run.id;
  return `
    <button class="sp-history-item" type="button" data-sp-lookup-open="${escapeAttr(run.id)}">
      <span><strong>${escapeHtml(spNumber)}</strong><small>${escapeHtml(new Date(run.created_at).toLocaleString())}</small></span>
      ${pill(labelize(run.status), run.status)}
    </button>
  `;
}

export function updateSpLookupHistoryCount(history: HTMLElement): void {
  const count = history.querySelectorAll("[data-sp-lookup-open]").length;
  const countElement = document.querySelector<HTMLElement>("[data-sp-lookup-history-count]");
  if (countElement) countElement.textContent = `${formatInteger(count)} loaded`;
}

export function spLookupResultHtml(run: ToolbenchRun): string {
  const result = run.result ?? {};
  const caseData = unknownRecord(result.case) ?? {};
  const facts = unknownRecord(result.facts) ?? {};
  const estimate = unknownRecord(result.loan_estimate) ?? {};
  const rate = unknownRecord(result.historical_rate) ?? {};
  const failure = unknownRecord(result.failure);
  const documents = Array.isArray(result.documents) ? result.documents.map(unknownRecord).filter((item): item is Record<string, unknown> => Boolean(item)) : [];
  const evidence = Array.isArray(result.evidence) ? result.evidence.map(unknownRecord).filter((item): item is Record<string, unknown> => Boolean(item)) : [];
  const estimatedValue = typeof estimate.expectedOpeningBidProxy === "number" ? estimate.expectedOpeningBidProxy : estimate.expected_opening_bid_proxy;
  const originalPrincipal = typeof facts.original_principal === "number" ? facts.original_principal : null;
  const status = String(result.lookup_status ?? run.status);
  const isSaleCancelled = status === "sale_cancelled" || status === "sale_canceled";
  const cancellation = unknownRecord(result.cancellation) ?? {};
  return `
    <article class="panel sp-result-card" data-sp-result-status="${escapeAttr(status)}">
      <div class="sp-result-heading">
        <div><p class="eyebrow">${escapeHtml(result.normalized_sp_number ?? run.input.spCode ?? "SP lookup")}</p><h2>${escapeHtml(result.summary ?? "Lookup result")}</h2></div>
        ${pill(labelize(status), status)}
      </div>
      ${isSaleCancelled ? spSaleCancelledHtml(cancellation, run.id) : typeof estimatedValue === "number" ? `<section class="sp-opening-bid"><span>Expected opening-bid proxy</span><strong>${formatCurrency(estimatedValue)}</strong><small>Estimated remaining scheduled principal—not a lender payoff quote.</small></section>` : ""}
      ${!isSaleCancelled && failure ? `<div class="sp-review-state"><strong>${escapeHtml(labelize(String(failure.class ?? "needs_review")))}</strong><p>${escapeHtml(failure.message ?? "This lookup needs review.")}</p></div>` : ""}
      <div class="sp-result-sections">
        ${spResultSection("Case", [
          ["SP number", caseData.sp_number ?? result.normalized_sp_number],
          ["County", caseData.county],
          ["Case type", caseData.case_type],
          ["Case status", caseData.status],
          ["Filed date", caseData.filed_date],
          ["Summary", caseData.summary]
        ])}
        ${spResultSection("Mortgage evidence", [
          ["Borrower / owner", facts.borrower_or_owner],
          ["Property address", facts.property_address],
          ["Original principal", originalPrincipal === null ? null : formatCurrency(originalPrincipal)],
          ["Original loan date", facts.original_loan_date],
          ["Principal source page", facts.original_principal_source_page ?? facts.source_page],
          ["Principal evidence", facts.original_principal_evidence_excerpt ?? facts.evidence_excerpt],
          ["Loan-date source page", facts.original_loan_date_source_page ?? facts.source_page],
          ["Loan-date evidence", facts.original_loan_date_evidence_excerpt ?? facts.evidence_excerpt]
        ])}
        ${isSaleCancelled ? "" : spResultSection("Present loan calculation", [
          ["Historical rate", typeof rate.annualRatePercent === "number" ? `${rate.annualRatePercent.toFixed(3)}%` : null],
          ["Scheduled payment", typeof estimate.scheduledMonthlyPayment === "number" ? formatCurrency(estimate.scheduledMonthlyPayment) : null],
          ["Payments counted", estimate.scheduledPaymentsMade],
          ["Payments through", estimate.paymentsThrough],
          ["Calculated at", estimate.calculatedAt],
          ["Remaining principal", typeof estimate.estimatedRemainingPrincipal === "number" ? formatCurrency(estimate.estimatedRemainingPrincipal) : null]
        ])}
      </div>
      <section class="sp-document-list"><h3>Documents</h3>${documents.map((document) => spDocumentHtml(document, run.id)).join("") || `<p class="empty">No Notice document has been attached yet.</p>`}</section>
      <section class="sp-evidence-list"><h3>Evidence collected</h3>${evidence.map((item) => spEvidenceHtml(item, run.id)).join("") || `<p class="empty">No additional evidence observations are available yet.</p>`}</section>
      <div class="sp-result-actions">
        <button class="button secondary" type="button" data-sp-lookup-open="${escapeAttr(run.id)}">Refresh result</button>
        ${run.status === "needs_review" ? `<button class="button" type="button" data-sp-lookup-retry="${escapeAttr(run.id)}">Retry lookup</button>` : ""}
      </div>
    </article>
  `;
}

export function spSaleCancelledHtml(cancellation: Record<string, unknown>, runId: string): string {
  const evidenceUrl = spArtifactUrl(cancellation.artifact_uri ?? cancellation.artifactUrl, "document", runId);
  return `<section class="sp-review-state sp-sale-cancelled"><strong>Sale canceled</strong><p>This is a terminal case status. No expected opening-bid proxy is shown for a canceled sale.</p><dl>${[
    ["Cancellation filing date", cancellation.filed_at ?? cancellation.filedDate],
    ["Cancellation document", cancellation.title ?? cancellation.document_title ?? cancellation.documentTitle],
    ["Evidence reference", cancellation.evidence_reference ?? cancellation.evidenceReference]
  ]
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "")
    .map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`)
    .join("")}</dl>${evidenceUrl ? `<a href="${escapeAttr(evidenceUrl)}" target="_blank" rel="noreferrer">Open cancellation evidence</a>` : ""}</section>`;
}

export function spResultSection(title: string, rows: Array<[string, unknown]>): string {
  const visible = rows.filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "");
  return `<section><h3>${escapeHtml(title)}</h3><dl>${visible.map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join("") || `<p class="empty">No values available.</p>`}</dl></section>`;
}

export function spDocumentHtml(value: Record<string, unknown>, runId: string): string {
  const url = typeof value.source_url === "string" && /^https?:\/\//.test(value.source_url) ? value.source_url : null;
  const documentUrl = spArtifactUrl(value.artifact_uri, "document", runId);
  const textUrl = spArtifactUrl(value.text_artifact_uri, "text", runId);
  const metadata = [
    value.filed_at ? `Filed ${String(value.filed_at)}` : null,
    value.retrieved_at ? `Retrieved ${String(value.retrieved_at)}` : null,
    value.sha256 ? `SHA-256 ${String(value.sha256).slice(0, 16)}…` : null,
    value.size_bytes ? `${formatInteger(Number(value.size_bytes))} bytes` : null,
    value.validation ? String(value.validation) : null
  ].filter(Boolean).join(" · ");
  return `<article><strong>${escapeHtml(value.title ?? value.document_type ?? "Notice document")}</strong><span>${escapeHtml(metadata || "Source metadata unavailable")}</span>${documentUrl ? `<a href="${escapeAttr(documentUrl)}" target="_blank" rel="noreferrer">Open archived PDF</a>` : ""}${textUrl ? `<a href="${escapeAttr(textUrl)}" target="_blank" rel="noreferrer">Open extracted text</a>` : ""}${url ? `<a href="${escapeAttr(url)}" target="_blank" rel="noreferrer">Open court portal</a>` : ""}</article>`;
}

export function spEvidenceHtml(value: Record<string, unknown>, runId: string): string {
  const manifestUrl = value.kind === "ecourts_lookup_manifest" ? spArtifactUrl(value.artifact_uri, "manifest", runId) : null;
  return `<article><strong>${escapeHtml(value.label ?? value.kind ?? "Evidence")}</strong><p>${escapeHtml(value.value ?? value.excerpt ?? "Source observation recorded.")}</p>${manifestUrl ? `<a href="${escapeAttr(manifestUrl)}" target="_blank" rel="noreferrer">Open retrieval receipt</a>` : ""}</article>`;
}

export function spArtifactUrl(artifactUri: unknown, kind: "document" | "text" | "manifest", runId = ""): string | null {
  if (typeof artifactUri !== "string" || !artifactUri.trim() || !runId) return null;
  return `/dashboard/toolbench/runs/${encodeURIComponent(runId)}/artifacts/${kind}?artifact_uri=${encodeURIComponent(artifactUri)}`;
}

